import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Brain, X, CircleNotch, Sparkle, ArrowBendDownLeft, Copy, WarningOctagon,
} from "@phosphor-icons/react";

// Lightweight markdown -> elements: supports **bold** and "- " bullets and blank-line paragraphs.
function renderAnalysis(text) {
  if (!text) return null;
  const blocks = text.split(/\n{2,}/);
  return blocks.map((block, bi) => {
    const lines = block.split("\n");
    const isList = lines.every((l) => l.trim().startsWith("- ") || l.trim() === "");
    if (isList) {
      return (
        <ul key={bi} className="list-disc pl-5 space-y-1 my-2">
          {lines.filter((l) => l.trim()).map((l, li) => (
            <li key={li} className="text-sm text-slate-700 leading-relaxed">{renderInline(l.replace(/^\s*-\s/, ""))}</li>
          ))}
        </ul>
      );
    }
    return <p key={bi} className="text-sm text-slate-700 leading-relaxed my-2">{renderInline(block)}</p>;
  });
}

function renderInline(s) {
  const parts = s.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

export default function CoPilotPanel({ open, onClose, docId, toolType = "focus-armor", step, phase, onApplySuggestion }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const reqId = useRef(0);

  const accent = phase === "ARMOR" ? "amber" : "blue";

  const fetchReview = useCallback(async () => {
    if (!docId || !step) return;
    const myReq = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.post(`/documents/${docId}/coincident-review`, {
        tool_type: toolType,
        currentStep: step.code,
        stepNumber: step.n,
      });
      if (myReq === reqId.current) setData(res.data);
    } catch (e) {
      if (myReq === reqId.current) setError("Tzu is unavailable right now. Try again in a moment.");
    } finally {
      if (myReq === reqId.current) setLoading(false);
    }
  }, [docId, toolType, step]);

  // Auto-fetch whenever the panel is open and the step changes
  useEffect(() => {
    if (open) fetchReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step?.n]);

  return (
    <div
      data-testid="copilot-drawer"
      className={`fixed top-0 right-0 h-screen w-full sm:w-[380px] bg-white border-l border-slate-200 shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-in-out ${
        open ? "translate-x-0" : "translate-x-full"
      }`}
      role="complementary"
      aria-hidden={!open}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-200 bg-slate-900 text-white">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${accent === "amber" ? "bg-amber-600" : "bg-blue-700"}`}>
            <Brain size={18} weight="duotone" />
          </div>
          <div>
            <p className="font-mono text-sm font-semibold tracking-tight leading-none">TZU · ADVISOR</p>
            <p className="font-mono text-[10px] text-slate-400 mt-1 uppercase tracking-wider">Socratic Red-Team Advisor</p>
          </div>
        </div>
        <button data-testid="copilot-close" onClick={onClose} className="p-1.5 rounded-sm hover:bg-slate-800 transition-colors">
          <X size={18} />
        </button>
      </div>

      {/* Step context bar */}
      <div className={`px-4 py-2 border-b border-slate-200 flex items-center justify-between ${accent === "amber" ? "bg-amber-50" : "bg-blue-50"}`}>
        <span className={`font-mono text-xs font-semibold ${accent === "amber" ? "text-amber-700" : "text-blue-700"}`}>
          STEP {step?.n} · {step?.code} — {step?.label}
        </span>
        <button data-testid="copilot-refresh" onClick={fetchReview} disabled={loading}
          className="font-mono text-[11px] text-slate-500 hover:text-slate-800 disabled:opacity-50 transition-colors">
          {loading ? "…" : "Re-run"}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading && (
          <div data-testid="copilot-loading" className="space-y-4">
            <div className="flex items-center gap-2 text-slate-500">
              <CircleNotch size={16} className="animate-spin" />
              <span className="font-mono text-xs tracking-wide animate-pulse">Analyzing framework alignment…</span>
            </div>
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-3 rounded-sm bg-slate-100 animate-pulse" style={{ width: `${90 - i * 8}%`, animationDelay: `${i * 80}ms` }} />
              ))}
            </div>
            <div className="h-px bg-slate-100 my-2" />
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-9 rounded-sm bg-slate-100 animate-pulse" style={{ animationDelay: `${i * 120}ms` }} />
              ))}
            </div>
          </div>
        )}

        {!loading && error && (
          <div data-testid="copilot-error" className="flex flex-col items-center text-center py-10 text-slate-500">
            <WarningOctagon size={28} className="text-amber-500 mb-2" />
            <p className="text-sm">{error}</p>
            <button onClick={fetchReview} className="mt-3 font-mono text-xs text-blue-700 hover:underline">Retry</button>
          </div>
        )}

        {!loading && !error && data && (
          <div className="animate-[fadeIn_0.25s_ease-in]">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkle size={14} weight="fill" className={accent === "amber" ? "text-amber-600" : "text-blue-600"} />
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">Analysis</span>
            </div>
            <div data-testid="copilot-analysis">{renderAnalysis(data.analysis)}</div>

            {data.actionableSuggestions?.length > 0 && (
              <div className="mt-5">
                <div className="flex items-center gap-1.5 mb-2">
                  <ArrowBendDownLeft size={14} weight="bold" className="text-slate-500" />
                  <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">Actionable Suggestions</span>
                </div>
                <div className="space-y-2" data-testid="copilot-suggestions">
                  {data.actionableSuggestions.map((s, i) => (
                    <button
                      key={i}
                      data-testid={`copilot-suggestion-${i}`}
                      onClick={() => onApplySuggestion?.(s)}
                      className="group w-full text-left rounded-sm border border-slate-200 bg-slate-50 hover:bg-white hover:border-blue-300 hover:-translate-y-px transition-all duration-200 p-3"
                    >
                      <div className="flex items-start gap-2">
                        <span className="font-mono text-[10px] font-bold text-slate-400 mt-0.5 group-hover:text-blue-600">{i + 1}</span>
                        <span className="text-sm text-slate-700 leading-snug flex-1">{s}</span>
                        <Copy size={14} className="text-slate-300 group-hover:text-blue-600 shrink-0 mt-0.5" />
                      </div>
                    </button>
                  ))}
                </div>
                <p className="mt-3 font-mono text-[10px] text-slate-400 leading-relaxed">
                  Click a suggestion to append it to the field on this step. Tzu advises — you decide.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function CoPilotToggle({ open, onClick, phase }) {
  if (open) return null;
  const accent = phase === "ARMOR" ? "amber" : "blue";
  return (
    <button
      data-testid="copilot-toggle"
      onClick={onClick}
      className={`fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center gap-2 rounded-l-md py-3 pl-3 pr-3.5 text-white shadow-lg hover:pr-5 transition-all duration-300 ${
        accent === "amber" ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-700 hover:bg-blue-800"
      }`}
      style={{ boxShadow: accent === "amber" ? "0 0 0 1px rgba(180,83,9,.4), 0 8px 24px rgba(180,83,9,.25)" : "0 0 0 1px rgba(29,78,216,.4), 0 8px 24px rgba(29,78,216,.25)" }}
    >
      <Brain size={20} weight="duotone" className="animate-pulse" />
      <span className="font-mono text-xs font-semibold tracking-wide [writing-mode:vertical-rl] rotate-180 sm:[writing-mode:horizontal-tb] sm:rotate-0">
        Ask Tzu
      </span>
    </button>
  );
}
